import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { todaySubject, tasks321, weeklyProgress, aiFeedbackExamples } from "../../mocks/learning";

// ── Today Popup ────────────────────────────────────────────────────────
function TodayPopup({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
      <div className="bg-white rounded-3xl w-full max-w-sm p-8 relative animate-fade-up">
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🌅</div>
          <h2 className="text-2xl font-black text-[#191F28] mb-2">오늘의 학습 시작!</h2>
          <p className="text-[#191F28]/50 text-sm leading-relaxed">
            딱 3가지 과제만 완료하면 오늘 끝이에요.
            <br />부담 없이, 하나씩 시작해봐요.
          </p>
        </div>
        <div className="space-y-3 mb-7">
          {tasks321.map((t) => (
            <div key={t.id} className="flex items-center gap-3 bg-[#F2F4F6] rounded-2xl px-4 py-3">
              <div className="w-7 h-7 rounded-full bg-[#00D873] flex items-center justify-center text-white text-xs font-black shrink-0">
                {t.number}
              </div>
              <span className="text-sm font-semibold text-[#191F28] leading-tight">{t.title}</span>
              <span className="ml-auto text-xs text-[#191F28]/40 whitespace-nowrap">{t.estimatedTime}</span>
            </div>
          ))}
        </div>
        <div className="bg-[#F2F4F6] rounded-2xl px-4 py-3 flex items-center justify-between mb-6">
          <span className="text-sm text-[#191F28]/60">총 예상 소요 시간</span>
          <span className="font-black text-[#191F28]">약 2시간</span>
        </div>
        <button
          onClick={onClose}
          className="w-full bg-[#00D873] text-white font-black text-lg py-4 rounded-2xl hover:opacity-90 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
        >
          바로 시작하기 →
        </button>
      </div>
    </div>
  );
}

// ── AI Feedback Modal ──────────────────────────────────────────────────
type TaskState = {
  id: number;
  status: "todo" | "submitting" | "ai_feedback" | "expert_pending" | "done";
  answer: string;
  aiScore: number | null;
  expertApproved: boolean;
  feedback: string;
};

function FeedbackModal({
  task,
  taskState,
  onSubmit,
  onExpertApprove,
  onClose,
}: {
  task: (typeof tasks321)[0];
  taskState: TaskState;
  onSubmit: (answer: string) => void;
  onExpertApprove: () => void;
  onClose: () => void;
}) {
  const [answer, setAnswer] = useState(taskState.answer);
  const [charCount, setCharCount] = useState(taskState.answer.length);

  const handleChange = (v: string) => {
    if (v.length <= 500) { setAnswer(v); setCharCount(v.length); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-[#F2F4F6] rounded-3xl w-full max-w-lg relative" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-white rounded-t-3xl px-7 pt-7 pb-5 border-b border-[#F2F4F6]">
          <button onClick={onClose} className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-[#191F28]/30 hover:text-[#191F28] cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-full bg-[#00D873] text-white text-sm font-black flex items-center justify-center">{task.number}</div>
            <span className="text-xs font-bold text-[#00D873] bg-[#00D873]/10 px-3 py-1 rounded-full">{task.category}</span>
            <span className="text-xs text-[#191F28]/40 flex items-center gap-1 ml-auto">
              <i className="ri-time-line" />{task.estimatedTime}
            </span>
          </div>
          <h3 className="text-lg font-black text-[#191F28] mt-2">{task.title}</h3>
          <p className="text-sm text-[#191F28]/60 mt-1 leading-relaxed">{task.desc}</p>
        </div>

        <div className="p-7 space-y-4">
          {/* Hint */}
          <div className="flex items-start gap-2 bg-[#FFD600]/10 rounded-2xl px-4 py-3">
            <span className="text-base shrink-0">💡</span>
            <p className="text-xs text-[#191F28]/70 leading-relaxed">{task.hint}</p>
          </div>

          {/* Answer Input */}
          {taskState.status === "todo" && (
            <>
              <div>
                <label className="text-xs font-semibold text-[#191F28]/60 block mb-2">과제 내용 / 사용한 프롬프트</label>
                <textarea
                  value={answer}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder="여기에 작성한 프롬프트나 과제 내용을 붙여넣기 해주세요..."
                  className="w-full bg-white rounded-2xl p-4 text-sm text-[#191F28] resize-none outline-none border-2 border-transparent focus:border-[#00D873] transition-colors min-h-[120px]"
                />
                <div className="text-xs text-[#191F28]/30 text-right mt-1">{charCount}/500</div>
              </div>
              <div className="bg-white rounded-2xl p-4 border-2 border-dashed border-[#191F28]/10 cursor-pointer hover:border-[#00D873] transition-colors text-center">
                <i className="ri-image-add-line text-2xl text-[#191F28]/20 block mb-1" />
                <span className="text-xs text-[#191F28]/40">결과 이미지 첨부 (선택)</span>
              </div>
              <button
                onClick={() => answer.trim() && onSubmit(answer)}
                disabled={!answer.trim()}
                className="w-full bg-[#191F28] text-white font-black py-4 rounded-2xl cursor-pointer hover:bg-[#00D873] transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
              >
                AI 피드백 받기 🤖
              </button>
            </>
          )}

          {/* AI Analyzing */}
          {taskState.status === "submitting" && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00D873]/10 mb-4">
                <i className="ri-robot-line text-3xl text-[#00D873] animate-pulse" />
              </div>
              <p className="font-bold text-[#191F28]">AI가 분석 중이에요...</p>
              <p className="text-xs text-[#191F28]/40 mt-1">보통 3~5초 정도 걸려요</p>
              <div className="flex justify-center gap-1 mt-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-2 h-2 rounded-full bg-[#00D873] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          )}

          {/* AI Feedback */}
          {(taskState.status === "ai_feedback" || taskState.status === "expert_pending" || taskState.status === "done") && (
            <div className="space-y-4">
              {/* AI Score */}
              <div className="bg-white rounded-2xl p-5 border-2 border-[#00D873]">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#00D873]/10 rounded-xl shrink-0">
                    <i className="ri-robot-line text-xl text-[#00D873]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-[#00D873]">AI 1차 피드백</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xl font-black text-[#191F28]">{taskState.aiScore}</span>
                        <span className="text-xs text-[#191F28]/40">/100</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#F2F4F6] rounded-full overflow-hidden mb-3">
                      <div className="h-full bg-[#00D873] rounded-full transition-all duration-1000" style={{ width: `${taskState.aiScore}%` }} />
                    </div>
                    <p className="text-sm text-[#191F28] leading-relaxed">{taskState.feedback}</p>
                  </div>
                </div>
              </div>

              {/* Expert Approval */}
              <div className={`bg-white rounded-2xl p-5 border-2 transition-all ${taskState.expertApproved ? "border-[#FFD600]" : "border-[#F2F4F6]"}`}>
                <div className="flex items-center gap-3">
                  <img
                    src={todaySubject.instructorAvatar}
                    alt={todaySubject.instructor}
                    className="w-10 h-10 rounded-full object-cover object-top"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#191F28]">{todaySubject.instructor}</span>
                      {taskState.expertApproved && (
                        <span className="text-xs bg-[#FFD600]/20 text-[#CC9900] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-award-line text-xs" />인증 완료
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#191F28]/40 mt-0.5">
                      {taskState.expertApproved ? "전문가 검토 완료 ✅" : "전문가 검토 대기 중..."}
                    </div>
                  </div>
                  {/* Demo: expert approve button */}
                  {!taskState.expertApproved && taskState.status === "expert_pending" && (
                    <button
                      onClick={onExpertApprove}
                      className="text-xs bg-[#FFD600] text-[#191F28] font-bold px-3 py-1.5 rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap"
                    >
                      인증하기 (데모)
                    </button>
                  )}
                </div>
              </div>

              {taskState.status === "done" && (
                <div className="bg-[#00D873] rounded-2xl p-5 text-center">
                  <div className="text-3xl mb-2">🏆</div>
                  <div className="font-black text-white text-lg">과제 최종 완료!</div>
                  <div className="text-white/80 text-sm mt-1">AI + 전문가 인증까지 모두 완료됐어요</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Task Card ──────────────────────────────────────────────────────────
function TaskCard({
  task,
  taskState,
  onOpen,
}: {
  task: (typeof tasks321)[0];
  taskState: TaskState;
  onOpen: () => void;
}) {
  const statusLabel = {
    todo: { text: "시작 전", color: "#191F28", bg: "#F2F4F6" },
    submitting: { text: "분석 중", color: "#00D873", bg: "#00D873/10" },
    ai_feedback: { text: "AI 검토완료", color: "#00D873", bg: "#00D873" },
    expert_pending: { text: "전문가 대기", color: "#CC9900", bg: "#FFD600" },
    done: { text: "완료 ✅", color: "#fff", bg: "#191F28" },
  }[taskState.status];

  const isDone = taskState.status === "done";

  return (
    <div
      className={`bg-white rounded-3xl p-6 lg:p-7 flex flex-col transition-all duration-300 cursor-pointer hover:-translate-y-1 border-2 ${isDone ? "border-[#00D873]/30 opacity-90" : "border-transparent"}`}
      onClick={onOpen}
    >
      {/* Number Badge */}
      <div className="flex items-center justify-between mb-5">
        <div className="w-10 h-10 rounded-2xl bg-[#00D873] flex items-center justify-center">
          <span className="text-white font-black text-lg">{task.number}</span>
        </div>
        <span
          className="text-xs font-bold px-3 py-1.5 rounded-full"
          style={{
            background: taskState.status === "done" ? "#191F28" : taskState.status !== "todo" ? "#00D873" : "#F2F4F6",
            color: taskState.status === "todo" ? "#191F28" : "#fff",
          }}
        >
          {statusLabel.text}
        </span>
      </div>

      {/* Icon */}
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00D873]/10 mb-4">
        <i className={`${task.icon} text-2xl text-[#00D873]`} />
      </div>

      <h3 className="text-base font-black text-[#191F28] leading-tight mb-2">{task.title}</h3>
      <p className="text-xs text-[#191F28]/55 leading-relaxed flex-1 mb-5">{task.desc}</p>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-[#F2F4F6]">
        <div className="flex items-center gap-1.5 text-xs text-[#191F28]/50">
          <i className="ri-time-line" />
          <span>{task.estimatedTime}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#191F28]/50">
          <i className="ri-bookmark-line" />
          <span>{task.category}</span>
        </div>
      </div>

      {/* Progress bar */}
      {taskState.aiScore !== null && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#191F28]/40">AI 점수</span>
            <span className="font-bold text-[#00D873]">{taskState.aiScore}/100</span>
          </div>
          <div className="h-1.5 bg-[#F2F4F6] rounded-full overflow-hidden">
            <div className="h-full bg-[#00D873] rounded-full" style={{ width: `${taskState.aiScore}%` }} />
          </div>
        </div>
      )}

      <button
        className={`mt-4 w-full py-3.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
          isDone
            ? "bg-[#F2F4F6] text-[#191F28]/40"
            : "bg-[#00D873] text-white hover:opacity-90"
        }`}
      >
        {isDone ? "완료됨 ✅" : "시작하기 →"}
      </button>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────
export default function LearningPage() {
  const [showPopup, setShowPopup] = useState(true);
  const [activeTask, setActiveTask] = useState<number | null>(null);
  const [taskStates, setTaskStates] = useState<TaskState[]>(
    tasks321.map((t) => ({
      id: t.id,
      status: "todo",
      answer: "",
      aiScore: null,
      expertApproved: false,
      feedback: "",
    }))
  );

  const completedCount = taskStates.filter((t) => t.status === "done").length;

  const handleSubmit = (taskId: number, answer: string) => {
    setTaskStates((prev) =>
      prev.map((t) => t.id === taskId ? { ...t, status: "submitting", answer } : t)
    );
    setTimeout(() => {
      const score = Math.floor(Math.random() * 20 + 78);
      const feedbackIdx = taskId - 1;
      setTaskStates((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, status: "expert_pending", aiScore: score, feedback: aiFeedbackExamples[feedbackIdx] }
            : t
        )
      );
    }, 2000);
  };

  const handleExpertApprove = (taskId: number) => {
    setTaskStates((prev) =>
      prev.map((t) => t.id === taskId ? { ...t, status: "done", expertApproved: true } : t)
    );
  };

  const activeTaskData = activeTask !== null ? tasks321.find((t) => t.id === activeTask) : null;
  const activeTaskState = activeTask !== null ? taskStates.find((t) => t.id === activeTask)! : null;

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 lg:px-6 pt-24 pb-16">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#191F28]/40 hover:text-[#191F28] transition-colors cursor-pointer">
            <i className="ri-arrow-left-line" /> 대시보드
          </Link>
        </div>

        {/* ── Subject Info ── */}
        <div className="bg-white rounded-3xl p-7 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs font-bold bg-[#00D873]/10 text-[#00D873] px-3 py-1 rounded-full">Week {todaySubject.week}</span>
                <span className="text-xs text-[#191F28]/40">Lesson {todaySubject.lesson}</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-[#191F28] leading-tight mb-1">{todaySubject.title}</h1>
              <div className="flex items-center gap-2 mt-3">
                <img src={todaySubject.instructorAvatar} alt={todaySubject.instructor} className="w-7 h-7 rounded-full object-cover object-top" />
                <span className="text-sm text-[#191F28]/60">{todaySubject.instructor}</span>
              </div>
            </div>
            {/* Progress donut */}
            <div className="text-center shrink-0">
              <div className="relative w-20 h-20 mx-auto">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="30" fill="none" stroke="#F2F4F6" strokeWidth="8" />
                  <circle
                    cx="40" cy="40" r="30" fill="none"
                    stroke="#00D873" strokeWidth="8"
                    strokeDasharray={`${(completedCount / 3) * 188} 188`}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-black text-[#191F28]">{completedCount}/3</span>
                </div>
              </div>
              <div className="text-xs text-[#191F28]/50 mt-2">오늘 완료</div>
            </div>
          </div>

          {/* Total time */}
          <div className="flex items-center gap-3 mt-5 pt-5 border-t border-[#F2F4F6]">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 flex items-center justify-center bg-[#F2F4F6] rounded-xl">
                <i className="ri-time-line text-[#191F28]/60" />
              </div>
              <span className="text-[#191F28]/60">총 예상 소요 시간</span>
              <span className="font-black text-[#191F28]">약 2시간</span>
            </div>
            <div className="ml-auto flex items-center gap-2 text-sm">
              <div className="w-8 h-8 flex items-center justify-center bg-[#F2F4F6] rounded-xl">
                <i className="ri-robot-line text-[#191F28]/60" />
              </div>
              <span className="text-[#191F28]/60">AI + 전문가 크로스 피드백</span>
            </div>
          </div>
        </div>

        {/* ── 321 Label ── */}
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-[#191F28] text-white font-black text-sm px-4 py-2 rounded-full">
            3·2·1 챌린지
          </div>
          <span className="text-sm text-[#191F28]/50">과제 하나씩 클릭해서 시작하세요</span>
          <div className="ml-auto flex gap-1.5">
            {tasks321.map((t) => {
              const s = taskStates.find((x) => x.id === t.id)!;
              return (
                <div key={t.id} className={`w-8 h-2 rounded-full transition-all ${s.status === "done" ? "bg-[#00D873]" : s.status !== "todo" ? "bg-[#00D873]/40" : "bg-[#F2F4F6]"}`} />
              );
            })}
          </div>
        </div>

        {/* ── 1도 부담 없는 과제 ── */}
        <div className="mb-2 px-1">
          <h2 className="text-3xl lg:text-4xl font-black text-[#191F28] leading-tight">
            1도 부담 없는
            <br />
            <span className="text-[#00D873]">오늘의 과제</span>
          </h2>
          <p className="text-[#191F28]/50 mt-2 text-sm">3개의 과제를 완료하면 오늘 학습이 끝나요.</p>
        </div>

        {/* ── Task Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {tasks321.map((task) => {
            const state = taskStates.find((s) => s.id === task.id)!;
            return (
              <TaskCard
                key={task.id}
                task={task}
                taskState={state}
                onOpen={() => setActiveTask(task.id)}
              />
            );
          })}
        </div>

        {/* ── Weekly Progress ── */}
        <div className="bg-white rounded-3xl p-7 mt-6">
          <h3 className="text-xl font-black text-[#191F28] mb-6">주차별 진행 현황</h3>
          <div className="space-y-3">
            {weeklyProgress.map((w) => (
              <div key={w.week} className={`flex items-center gap-4 p-4 rounded-2xl ${w.week === todaySubject.week ? "bg-[#00D873]/5 border-2 border-[#00D873]/20" : "bg-[#F2F4F6]"}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black shrink-0 ${w.completed ? "bg-[#00D873] text-white" : w.week === todaySubject.week ? "bg-[#00D873]/20 text-[#00D873]" : "bg-white text-[#191F28]/30"}`}>
                  {w.completed ? "✓" : w.week}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-[#191F28]">{w.title}</div>
                  {w.week === todaySubject.week && <div className="text-xs text-[#00D873] font-semibold mt-0.5">진행 중</div>}
                </div>
                {w.score && <div className="text-sm font-black text-[#00D873]">{w.score}점</div>}
                {!w.completed && w.week !== todaySubject.week && <div className="text-xs text-[#191F28]/30">미시작</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPopup && <TodayPopup onClose={() => setShowPopup(false)} />}
      {activeTaskData && activeTaskState && (
        <FeedbackModal
          task={activeTaskData}
          taskState={activeTaskState}
          onSubmit={(ans) => handleSubmit(activeTaskData.id, ans)}
          onExpertApprove={() => { handleExpertApprove(activeTaskData.id); }}
          onClose={() => setActiveTask(null)}
        />
      )}
    </div>
  );
}
